import React, { useEffect, useState, useRef } from "react";
import { ChevronDown } from "lucide-react";
import { Domain } from "@/types/common";

const LIVE_DOMAINS: Domain[] = [
  {
    id: "1",
    name: "INDIA",
    url: "https://www.peoplematters.in",
    image:
      "https://asset.peoplematters.in/images/8e66aaad-d50a-4b9e-8266-7c621928d32a.png",
  },
  {
    id: "2",
    name: "ANZ",
    url: "https://anz.peoplemattersglobal.com",
    image:
      "https://asset.peoplematters.in/images/1b2fe5ba-3702-4b87-9191-3a73a99dc3b8.png",
  },
  {
    id: "3",
    name: "SEA",
    url: "https://sea.peoplemattersglobal.com",
    image:
      "https://asset.peoplematters.in/images/d3b87aaa-718d-420d-90a6-1af2da4e77bf.png",
  },
  {
    id: "4",
    name: "ME",
    url: "https://me.peoplemattersglobal.com",
    image:
      "https://asset.peoplematters.in/images/f82bc1c5-5426-4aa3-ac38-b01524d212cf.png",
  },
];

const BETA_DOMAINS: Domain[] = [
  {
    id: "1",
    name: "INDIA",
    url: "https://beta.peoplematters.in",
    image:
      "https://asset.peoplematters.in/images/8e66aaad-d50a-4b9e-8266-7c621928d32a.png",
  },
  {
    id: "2",
    name: "ANZ",
    url: "https://beta-anz.peoplemattersglobal.com",
    image:
      "https://asset.peoplematters.in/images/1b2fe5ba-3702-4b87-9191-3a73a99dc3b8.png",
  },
  {
    id: "3",
    name: "SEA",
    url: "https://beta.peoplemattersglobal.com",
    image:
      "https://asset.peoplematters.in/images/d3b87aaa-718d-420d-90a6-1af2da4e77bf.png",
  },
  {
    id: "4",
    name: "ME",
    url: "https://beta-me.peoplemattersglobal.com",
    image:
      "https://asset.peoplematters.in/images/f82bc1c5-5426-4aa3-ac38-b01524d212cf.png",
  },
];

const UAT_DOMAINS: Domain[] = [
  {
    id: "1",
    name: "INDIA",
    url: "https://uat-india-pm.enpointe.io",
    image:
      "https://asset.peoplematters.in/images/8e66aaad-d50a-4b9e-8266-7c621928d32a.png",
  },
  {
    id: "2",
    name: "ANZ",
    url: "https://uat-anz-pm.enpointe.io",
    image:
      "https://asset.peoplematters.in/images/1b2fe5ba-3702-4b87-9191-3a73a99dc3b8.png",
  },
  {
    id: "3",
    name: "SEA",
    url: "https://uat-global-pm.enpointe.io",
    image:
      "https://asset.peoplematters.in/images/d3b87aaa-718d-420d-90a6-1af2da4e77bf.png",
  },
  {
    id: "4",
    name: "ME",
    url: "https://uat-me-pm.enpointe.io",
    image:
      "https://asset.peoplematters.in/images/f82bc1c5-5426-4aa3-ac38-b01524d212cf.png",
  },
];

type CountryDropdownProps = {
  hostname?: string;
};

const getDomainList = (hostname: string) => {
  switch (true) {
    case hostname.startsWith("uat-"):
      return UAT_DOMAINS;
    case hostname.startsWith("beta.") || hostname.startsWith("beta-"):
      return BETA_DOMAINS;
    default:
      return LIVE_DOMAINS;
  }
};

const pickSelectedDomain = (list: Domain[], hostname: string) => {
  const selected = list.find(
    (domain) => domain.url && new URL(domain.url).hostname === hostname
  );
  return selected || list[0];
};

const CountryDropdown = ({ hostname = "" }: CountryDropdownProps) => {
  const initialList = getDomainList(hostname);
  const initialSelected = pickSelectedDomain(initialList, hostname);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Domain>(initialSelected);
  const domainList = initialList;
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const selectCountry = (country: Domain) => {
    setSelectedCountry(country);
    setIsOpen(false);
    
    // Redirect to the selected domain's URL
    if (country.url) {
      window.location.href = country.url;
    }
  };

  // Handle clicking outside the dropdown and scrolling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    // Handle scrolling to close dropdown
    const handleScroll = () => {
      setIsOpen(false);
    };

    // Handle wheel events for better scroll detection
    const handleWheel = () => {
      setIsOpen(false);
    };

    // Handle touch events for mobile scrolling
    const handleTouchMove = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleScroll, { passive: true });
      document.addEventListener('wheel', handleWheel, { passive: true });
      document.addEventListener('touchmove', handleTouchMove, { passive: true });
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('wheel', handleWheel, { passive: true });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll);
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleWheel);
    };
  }, [isOpen]);

  return (
    <div className="relative w-20" ref={dropdownRef}>
      {/* Closed dropdown state */}
      <div
        className="flex items-center justify-start gap-1 bg-indigo text-white cursor-pointer p-1"
        onClick={toggleDropdown}
      >
        <div className="flex items-center gap-2">
          {/* <Image
            src={selectedCountry?.image || "/assets/images/dummy.jpg"}
            alt={selectedCountry?.name || "Country image"}
            width={24}
            height={16}
          /> */}
          <span className="text-sm font-medium mx-1">
            {selectedCountry?.name}
          </span>
        </div>
        {domainList.length > 1 && <ChevronDown className="w-3 h-3" />}
      </div>

      {/* Dropdown options */}
      {isOpen && (
        <div className="absolute w-20 text-left bg-indigo text-white z-10">
          {domainList
            .filter((country) => country.name !== selectedCountry?.name)
            .map((country) => (
              <div
                key={country.name}
                className="flex items-center gap-2 p-1 hover:bg-gray-800 cursor-pointer"
                onClick={() => selectCountry(country)}
              >
                {/* <Image
                  src={country?.image || "/assets/images/dummy.jpg"}
                  alt={country.name || "Country image"}
                  width={24}
                  height={16}
                  className="object-cover rounded-sm"
                /> */}
                <span className="text-sm font-medium ml-1">{country.name}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default CountryDropdown;
